import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import List from '@material-ui/core/List';
import Icon from 'components/Utils/NewIcon';
import UIUtils from 'services/UIUtils';
import PatientBadge from 'components/Patient/PatientBadge';
import DefaultTextField from 'components/Utils/DefaultTextField';
import SelectableListItem from 'components/Utils/SelectableListItem';
import ProgressMessage from 'components/Utils/ProgressMessage';
import DefaultButton from 'components/Utils/NewDefaultButton';
import _ from 'lodash';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import useFetchPatients from 'hooks/useFetchPatients';
import { GetStaff } from 'careand-redux/selectors';
import PatientUtils from 'services/PatientUtils';
import StringUtils from 'services/StringUtils';

let lastSearch;
const NUMBER_RESULTS = 25;
const DEFAULT_ARRAY = [];

const PatientSearch = (props) => {
  const { showRecent, showFavorites } = props;

  const dispatch = useAppDispatch();

  const allPatients = useAppSelector((state) => state.patients.byId);
  const ownClinic = useAppSelector((state) => state.resource.clinic);
  const lastPatients = useAppSelector((state) => state.patients.lastPatients);
  const ownUser = useAppSelector(GetStaff);

  const [typingTimeout, setTypingTimeout] = useState(undefined);
  const [searchTerm, setSearchTerm] = useState(props.initialValue);
  const [clickedPatient, setClickedPatient] = useState(undefined);
  const [searching, setSearching] = useState(false);
  const [numberResults, setNumberResults] = useState(NUMBER_RESULTS);

  const favoritePatients = ownUser.favorite_patients || DEFAULT_ARRAY;

  const missingPatients = useMemo(() => {
    return [...favoritePatients, ...lastPatients].filter((id) => !allPatients[id]);
  }, [allPatients, favoritePatients, lastPatients]);

  useFetchPatients(undefined, missingPatients);

  const defaultPatients = useMemo(() => {
    let result = [];

    if (showRecent === true) {
      result = [
        ...result,
        ...lastPatients.map((id) => allPatients[id]).filter((patient) => !!patient),
      ];
    }

    if (showFavorites === true) {
      result = [
        ...result,
        ...favoritePatients.map((id) => allPatients[id]).filter((patient) => !!patient),
      ];
    }
    return _.uniqBy(result, 'id');
  }, [allPatients, favoritePatients, showRecent, lastPatients, showFavorites]);

  const [results, setResults] = useState(handleResults(defaultPatients, ownClinic, false));

  function handleResults(patients, clinic) {
    return {
      sameClinic: patients.filter(({ clinic_id }) => clinic_id === clinic?.id),
      otherClinics: patients.filter(({ clinic_id }) => clinic_id !== clinic?.id),
    };
  }

  useEffect(() => {
    if (!searchTerm && (!!showFavorites || !!showRecent)) {
      setResults(handleResults(defaultPatients, ownClinic, false));
    }
  }, [showRecent, showFavorites, searchTerm, defaultPatients, ownClinic]);

  useEffect(
    () => {
      if (!!props.initialValue) searchPatients(props.initialValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.initialValue],
  );

  async function enterPressed(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      clearTimeout(typingTimeout);
      if (searchTerm !== undefined && searchTerm !== '') await searchPatients(searchTerm);
    }
  }

  function handleSearchChange(event) {
    clearTimeout(typingTimeout);
    let value = event.target.value;
    setSearchTerm(value);
    setNumberResults(NUMBER_RESULTS);

    let timeoutHandle = setTimeout(async () => {
      if (value !== undefined && value !== '') await searchPatients(value);
    }, UIUtils.SEARCH_TYPE_TIMEOUT);

    setTypingTimeout(timeoutHandle);
  }

  async function searchPatients(value, limit = numberResults) {
    lastSearch = value;
    setSearching(true);
    let result = await dispatch(
      api.Patients.getPatients({ search: (value || '').toLowerCase(), limit }),
    );
    if (result && lastSearch === value) {
      setResults(handleResults(result, ownClinic));
    }
    setSearching(false);
  }

  function selectPatient(patient) {
    setClickedPatient(patient);
    if (props.onSelect !== undefined) props.onSelect(patient);
  }

  const allowLoadMore = results.sameClinic.length + results.otherClinics.length < numberResults;

  return (
    <div className="search-column full-height-panel scroll-list">
      <div className="search-field">
        {!searching && <Icon src="magnifying-glass" />}
        <ProgressMessage size={20} show={searching} />
        <DefaultTextField
          name="searchTerm"
          autoFocus={true}
          label="Search by name"
          value={searchTerm}
          onKeyDown={enterPressed}
          onChange={handleSearchChange}
        />
      </div>
      <div className="list">
        <List>
          <SearchResults
            patients={results.sameClinic}
            title={ownClinic?.name}
            selectPatient={selectPatient}
            customBadge={props.customBadge}
            clickedPatient={clickedPatient}
          />
          <SearchResults
            patients={results.otherClinics}
            title="Other Clinics"
            selectPatient={selectPatient}
            customBadge={props.customBadge}
            clickedPatient={clickedPatient}
          />
          <DefaultButton
            name="Load More"
            hide={allowLoadMore}
            onClick={() => {
              setNumberResults(numberResults + NUMBER_RESULTS);
              searchPatients(searchTerm, numberResults + NUMBER_RESULTS);
            }}
            style={{
              display: 'block',
              margin: '20px auto',
            }}
          />
        </List>
      </div>
    </div>
  );
};

const SearchResults = ({ patients, title, clickedPatient, selectPatient, customBadge }) => {
  return (
    patients.length > 0 && (
      <>
        <span className="small-title ml-4 mt-2">{title}</span>
        {patients.map((patient) => (
          <SelectableListItem
            key={patient.id}
            selected={clickedPatient?.id === patient.id}
            onClick={() => selectPatient(patient)}
            item={<SearchBadge customBadge={customBadge} patient={patient} />}
          />
        ))}
      </>
    )
  );
};

const SearchBadge = ({ customBadge, patient }) => (
  <>
    {customBadge ? customBadge(patient) : null}
    {!customBadge && <PatientBadge patient={patient} />}
  </>
);

PatientSearch.propTypes = {
  onSelect: PropTypes.func,
  showFavorites: PropTypes.bool,
  showRecent: PropTypes.bool,
  customBadge: PropTypes.func,
  initialValue: PropTypes.string,
};

export default PatientSearch;
